import React, { useEffect, useState } from "react";
import { ClipboardCheck, Sparkles } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function Intake() {
  const [clients, setClients] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [form, setForm] = useState({ client_id: "", phq9_score: "", gad7_score: "", pcl5_score: "", columbia_score: "", responses: {} });
  const [responseText, setResponseText] = useState("");
  const [gen, setGen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/intake").then(({ data }) => setIntakes(data));
  }, []);

  const submit = async () => {
    if (!form.client_id) return toast.error("Select a client");
    setGen(true);
    try {
      const payload = {
        client_id: form.client_id,
        phq9_score: form.phq9_score ? parseInt(form.phq9_score) : null,
        gad7_score: form.gad7_score ? parseInt(form.gad7_score) : null,
        pcl5_score: form.pcl5_score ? parseInt(form.pcl5_score) : null,
        columbia_score: form.columbia_score ? parseInt(form.columbia_score) : null,
        responses: { intake_narrative: responseText }
      };
      const { data } = await api.post("/intake/submit", payload);
      setIntakes([data, ...intakes]);
      setActive(data);
      toast.success("Intake submitted and summarized");
    } catch { toast.error("Submission failed"); }
    setGen(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ScoreInput = ({ label, name, max, testid }) => (
    <div>
      <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>{label} (0-{max})</label>
      <input type="number" min={0} max={max} value={form[name]} onChange={set(name)} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid={testid} />
    </div>
  );

  return (
    <Page title="Smart Intake" subtitle="Auto-scored PHQ-9, GAD-7, PCL-5, Columbia. AI-generated clinical summary.">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card delay={0.05} testid="intake-form">
          <h2 className="font-serif text-2xl mb-4">New Intake</h2>
          <div className="space-y-3">
            <select value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="intake-client-select">
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <ScoreInput label="PHQ-9" name="phq9_score" max={27} testid="intake-phq9" />
              <ScoreInput label="GAD-7" name="gad7_score" max={21} testid="intake-gad7" />
              <ScoreInput label="PCL-5" name="pcl5_score" max={80} testid="intake-pcl5" />
              <ScoreInput label="Columbia C-SSRS" name="columbia_score" max={5} testid="intake-columbia" />
            </div>
            <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} rows={5} placeholder="Client responses / narrative" className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="intake-responses"></textarea>
            <PrimaryButton onClick={submit} disabled={gen} testid="intake-submit-btn">
              {gen ? "Summarizing…" : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Submit & Summarize</span>}
            </PrimaryButton>
          </div>
        </Card>

        <Card delay={0.1} testid="intake-preview">
          <h2 className="font-serif text-2xl mb-4">Clinical Summary</h2>
          {active ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {Object.entries(active.interpretations || {}).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                    <div className="text-xs uppercase tracking-wider" style={{ color: "var(--mf-text-muted)" }}>{k}</div>
                    <div className="font-serif text-2xl" style={{ color: "var(--mf-primary)" }}>{v.score}</div>
                    <div className="text-xs">{v.severity}</div>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl text-sm whitespace-pre-wrap mf-prose" style={{ background: "var(--mf-muted)" }}>{active.ai_summary}</div>
            </div>
          ) : (
            <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>Submit an intake to see scores and summary.</div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">Intake History</h2>
        {intakes.length === 0 ? <EmptyState icon={ClipboardCheck} title="No intakes yet" description="Score assessments and get AI-generated summaries." /> : (
          <div className="grid md:grid-cols-2 gap-4">
            {intakes.map((it, i) => (
              <div key={it.id} className="mf-card p-5 cursor-pointer mf-fade-in" style={{ animationDelay: `${Math.min(i*0.05,0.3)}s` }} onClick={() => setActive(it)} data-testid={`intake-item-${it.id}`}>
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.entries(it.interpretations || {}).map(([k, v]) => <Pill key={k} variant="primary">{k}: {v.score}</Pill>)}
                </div>
                <div className="text-xs mb-1" style={{ color: "var(--mf-text-muted)" }}>{new Date(it.created_at).toLocaleDateString()}</div>
                <div className="text-sm line-clamp-2">{it.ai_summary?.slice(0, 250)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
